/**
 * Mirrors of the backend link-tracking enums
 * (microservice-user-profile `app/utils/link_tracking/enums.py`).
 *
 * Every fixed value the tracker sends or reads is spelled here once. Colours,
 * labels and error copy are keyed by these constants, never by string literals.
 *
 * `enums.snapshot.json` is the output of
 * `python -m app.utils.link_tracking.export_enums`, committed in both repos.
 * `enums.test.js` fails when this file and the snapshot disagree, so a value
 * changed on one side breaks CI until the other side follows.
 */

export const LINK_KIND = Object.freeze({
  QR: 'qr',
  URL: 'url',
  BOTH: 'both',
});

export const LINK_TYPE = Object.freeze({
  DEVICE: 'device',
  PLACE: 'place',
  MARKETING: 'marketing',
});

export const SOURCE_TYPE = Object.freeze({
  QR: 'qr',
  LINK: 'link',
});

export const DEVICE_TYPE = Object.freeze({
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
  OTHER: 'other',
});

export const HIT_CLASS = Object.freeze({
  HUMAN: 'human',
  BOT: 'bot',
  PREVIEW: 'preview',
  EMAIL_SCANNER: 'email_scanner',
});

export const CLASS_RULE = Object.freeze({
  UA: 'ua',
  HEADLESS: 'headless',
  SCANNER: 'scanner',
  REPEAT: 'repeat',
  DEFAULT: 'default',
});

/** Derived on the server (A35), never stored. */
export const LINK_STATUS = Object.freeze({
  ACTIVE: 'active',
  DISABLED: 'disabled',
  EXPIRED: 'expired',
});

export const AUDITED_FIELD = Object.freeze({
  SLUG: 'slug',
  NAME: 'name',
  KIND: 'kind',
  DESTINATION_URL: 'destination_url',
  CAMPAIGN_ID: 'campaign_id',
  CHANNEL: 'channel',
  TYPE: 'type',
  HOME_LOCATION: 'home_location',
  UTM_ENABLED: 'utm_enabled',
  UTM_SOURCE: 'utm_source',
  UTM_MEDIUM: 'utm_medium',
  UTM_CAMPAIGN: 'utm_campaign',
  INTERSTITIAL_ENABLED: 'interstitial_enabled',
  EXPIRES_AT: 'expires_at',
  ACTIVE: 'active',
  // Lead forms (X3): destination, form and offer edits are audited too.
  DESTINATION_TYPE: 'destination_type',
  FORM_ID: 'form_id',
  OFFER_TEXT: 'offer_text',
  OFFER_ENDS_AT: 'offer_ends_at',
});

/** TrackedLink.destination_type (FL2): redirect to a website, or open a lead form. */
export const DESTINATION_TYPE = Object.freeze({
  URL: 'url',
  LEAD_FORM: 'form',
});

export const DEPARTMENT = Object.freeze({
  EXECUTIVE: 'Executive',
  MARKETING: 'Marketing',
});

export const QR_FORMAT = Object.freeze({
  PNG: 'png',
  SVG: 'svg',
});

export const QR_ECC = Object.freeze({
  M: 'M',
  H: 'H',
});

export const LABEL_TEMPLATE = Object.freeze({
  A4_21_63X38: 'a4_21_63x38',
  A4_24_70X37: 'a4_24_70x37',
  A4_12_63X72: 'a4_12_63x72',
  AVERY_22806: 'avery_22806',
  AVERY_22805: 'avery_22805',
});

export const EXPORT_KIND = Object.freeze({
  HITS: 'hits',
  CAMPAIGN_SUMMARY: 'campaign_summary',
});

/** Every `detail.code` in Phase 2 section 1.2. */
export const LINK_ERROR_CODE = Object.freeze({
  LINK_NOT_FOUND: 'LINK_NOT_FOUND',
  CAMPAIGN_NOT_FOUND: 'CAMPAIGN_NOT_FOUND',
  CAMPAIGN_NAME_TAKEN: 'CAMPAIGN_NAME_TAKEN',
  SLUG_TAKEN: 'SLUG_TAKEN',
  SLUG_IMMUTABLE: 'SLUG_IMMUTABLE',
  DESTINATION_INVALID: 'DESTINATION_INVALID',
  DESTINATION_IS_TRACKED_LINK: 'DESTINATION_IS_TRACKED_LINK',
  LEAD_FORM_NOT_FOUND: 'LEAD_FORM_NOT_FOUND',
  UTM_TOKEN_INVALID: 'UTM_TOKEN_INVALID',
  UTM_EXTERNAL_DOMAIN_UNCONFIRMED: 'UTM_EXTERNAL_DOMAIN_UNCONFIRMED',
  EXPIRES_IN_PAST: 'EXPIRES_IN_PAST',
  OPERATION_NOT_ALLOWED: 'OPERATION_NOT_ALLOWED',
  BULK_ENABLE_NOT_ALLOWED: 'BULK_ENABLE_NOT_ALLOWED',
  CONFIRM_REQUIRED: 'CONFIRM_REQUIRED',
  STALE_UPDATE: 'STALE_UPDATE',
  CONCURRENT_UPDATE: 'CONCURRENT_UPDATE',
  IDEMPOTENCY_IN_PROGRESS: 'IDEMPOTENCY_IN_PROGRESS',
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  MODIFY_TIMEOUT: 'MODIFY_TIMEOUT',
  LABEL_TEMPLATE_UNKNOWN: 'LABEL_TEMPLATE_UNKNOWN',
  TOO_MANY_LABELS: 'TOO_MANY_LABELS',
  DATE_RANGE_INVALID: 'DATE_RANGE_INVALID',
  DATE_RANGE_TOO_LONG: 'DATE_RANGE_TOO_LONG',
  CURSOR_INVALID: 'CURSOR_INVALID',
  FORBIDDEN_DEPARTMENT: 'FORBIDDEN_DEPARTMENT',
  EXPORT_FORBIDDEN: 'EXPORT_FORBIDDEN',
  IP_ACCESS_FORBIDDEN: 'IP_ACCESS_FORBIDDEN',
  DEPARTMENT_LOOKUP_UNAVAILABLE: 'DEPARTMENT_LOOKUP_UNAVAILABLE',
  RATE_LIMITED: 'RATE_LIMITED',
  RENDER_BUSY: 'RENDER_BUSY',
  ANALYTICS_BUSY: 'ANALYTICS_BUSY',
  EXPORT_BUSY: 'EXPORT_BUSY',
  QUERY_TIMEOUT: 'QUERY_TIMEOUT',
});

/** S15 `ping_status` (A57): did the check page's script report back. */
export const PING_STATUS = Object.freeze({
  RECEIVED: 'received',
  PENDING: 'pending',
  MISSING: 'missing',
});

/** S15 / S19 `sort_by`. `ip` is Executive-only (U11, see `ipParams.js`). */
export const HIT_LOG_SORT = Object.freeze({
  HIT_AT: 'hit_at',
  IP: 'ip',
});

/** `sort_order` on every sortable list endpoint. */
export const SORT_ORDER = Object.freeze({
  ASC: 'asc',
  DESC: 'desc',
});

/** S2 `sort_by`. */
export const LINK_SORT_BY = Object.freeze({
  CREATED_AT: 'created_at',
  NAME: 'name',
  HUMAN_HITS: 'human_hits',
  TOTAL_HITS: 'total_hits',
  LAST_HIT_AT: 'last_hit_at',
});

/** S8 `sort_by`. */
export const CAMPAIGN_SORT_BY = Object.freeze({
  NAME: 'name',
  CREATED_AT: 'created_at',
  START_DATE: 'start_date',
});

/** S4 `warnings` values (Phase 2 4.4). */
export const LINK_WARNING = Object.freeze({
  DESTINATION_CHANGED_HITS_KEEP_HISTORY: 'DESTINATION_CHANGED_HITS_KEEP_HISTORY',
});

/** Snapshot name → mirror. Keys match `export_enums` output exactly. */
export const LINK_ENUMS = Object.freeze({
  AUDITED_FIELD,
  CAMPAIGN_SORT_BY,
  CLASS_RULE,
  DEPARTMENT,
  DESTINATION_TYPE,
  DEVICE_TYPE,
  EXPORT_KIND,
  HIT_CLASS,
  HIT_LOG_SORT,
  LABEL_TEMPLATE,
  LINK_ERROR_CODE,
  LINK_KIND,
  LINK_SORT_BY,
  LINK_STATUS,
  LINK_TYPE,
  LINK_WARNING,
  PING_STATUS,
  QR_ECC,
  QR_FORMAT,
  SORT_ORDER,
  SOURCE_TYPE,
});

/** The automated classes (everything but human), in the M19 fixed order. */
export const AUTOMATED_CLASSES = Object.freeze([
  HIT_CLASS.BOT,
  HIT_CLASS.PREVIEW,
  HIT_CLASS.EMAIL_SCANNER,
]);

/** M19 tie-break order. */
export const HIT_CLASS_ORDER = Object.freeze([HIT_CLASS.HUMAN, ...AUTOMATED_CLASSES]);
